import { CountryCode } from '@dayetopia/types';
import Colors from '@components/shared/Colors';
import {
  SYMPTOM_CHLAMYDIA_COLOR_IMG,
  SYMPTOM_GONORRHOEA_COLOR_IMG,
  SYMPTOM_TRICHOMONIASIS_COLOR_IMG,
  SYMPTOM_HERPES_COLOR_IMG,
  SYMPTOM_HPV_COLOR_IMG,
  SYMPTOM_ANAEROBIC_MICROORGANISMS_COLOR_IMG,
  SYMPTOM_LACTOBACILLUS_COLOR_IMG,
  SYMPTOM_MYCOPLASMA_UREAPLASMA_COLOR_IMG,
  SYMPTOM_CANDIDA_COLOR_IMG
} from '@constants';
import { PathogenCodes, PathogenDataItem, ScreeningType } from '@contracts';
import { showInCountryCondition } from '@utils/country';

export const PATHOGENS_DATA: PathogenDataItem[] = [
  {
    code: PathogenCodes.hpv_screening,
    shortInfo: 'symptom.hpv-screening-short',
    title: 'symptom.hpv-screening',
    background: Colors.ecru50,
    image: SYMPTOM_HPV_COLOR_IMG,
    info: 'symptom.hpv-screening-info',
    comingSoonCountryFilter: 'US',
    symptoms: ['symptom.not-usually'],
    onlyHPV: true
  },
  {
    code: PathogenCodes.lactobacilli,
    shortInfo: 'symptom.lactobacilli-short',
    title: 'symptom.lactobacillus',
    image: SYMPTOM_LACTOBACILLUS_COLOR_IMG
  },
  {
    code: PathogenCodes.anaerobic_bacteria,
    shortInfo: 'symptom.anaerobic-bacteria-short',
    title: 'symptom.anaerobic-microorganism',
    image: SYMPTOM_ANAEROBIC_MICROORGANISMS_COLOR_IMG
  },
  {
    code: PathogenCodes.candida,
    shortInfo: 'symptom.candida-short',
    title: 'symptom.candida',
    image: SYMPTOM_CANDIDA_COLOR_IMG
  },
  {
    code: PathogenCodes.mycoplasma_ureaplasma,
    shortInfo: 'symptom.mycoplasma-ureaplasma-short',
    title: 'symptom.mycoplasma-dash-ureaplasma',
    image: SYMPTOM_MYCOPLASMA_UREAPLASMA_COLOR_IMG
  },
  {
    code: PathogenCodes.chlamydia,
    shortInfo: 'symptom.chlamydia-short',
    title: 'symptom.chlamydia',
    color: Colors.carnationPink,
    background: Colors.carnationPink50,
    image: SYMPTOM_CHLAMYDIA_COLOR_IMG,
    info: 'symptom.chlamydia-info',
    comingSoonCountryFilter: 'US',
    symptoms: ['symptom.increased-discharge', 'symptom.bleeding-post-and', 'symptom.lower-pain', 'symptom.genital-pain'],
    onlySTI: true
  },
  {
    code: PathogenCodes.gonorrhoea,
    shortInfo: 'symptom.gonorrhoea-short',
    title: 'symptom.gonorrhoea',
    background: Colors.powderPink,
    image: SYMPTOM_GONORRHOEA_COLOR_IMG,
    info: 'symptom.gonorrhoea-info',
    comingSoonCountryFilter: 'US',
    symptoms: ['symptom.painful-urination', 'symptom.inc-vag-discharge', 'symptom.lower-pain', 'symptom.anal-discharge'],
    onlySTI: true
  },
  {
    code: PathogenCodes.trichomoniasis,
    shortInfo: 'symptom.trichomoniasis-short',
    title: 'symptom.trichomoniasis',
    background: Colors.paleGold50,
    image: SYMPTOM_TRICHOMONIASIS_COLOR_IMG,
    info: 'symptom.trichomoniasis-info',
    comingSoonCountryFilter: 'US',
    symptoms: ['symptom.ab-vag-discharge', 'symptom.unpl-fishy-smell', 'symptom.soreness-and-itching', 'symptom.pain-during-sex'],
    onlySTI: true
  },
  {
    code: PathogenCodes.herpes_with_types,
    shortInfo: 'symptom.herpes-with-types-short',
    title: 'symptom.herpes-with-types',
    background: Colors.babyBlue,
    image: SYMPTOM_HERPES_COLOR_IMG,
    info: 'symptom.herpes-with-types-info',
    comingSoon: true,
    symptoms: ['symptom.painful-sores', 'symptom.intching-buring', 'symptom.fever-headache', 'symptom.pain-when-peeing'],
    onlySTI: true
  }
];

/**
 * Gets the pathogens data for the given options.
 * @param options - Optional object containing pathogen codes, screening type, and country.
 * @returns Array of pathogen data items corresponding to the provided options, or all items if no options are provided.
 */
export function getPathogensData(options?: { codes?: PathogenCodes[]; screeningType?: ScreeningType; country?: CountryCode }): PathogenDataItem[] {
  let pathogensData = PATHOGENS_DATA;
  const { codes, screeningType, country } = options || {};

  if (screeningType && screeningType !== ScreeningType.Hpv) {
    const screeningOrder = {
      [ScreeningType.Sti]: [
        PathogenCodes.chlamydia,
        PathogenCodes.gonorrhoea,
        PathogenCodes.trichomoniasis,
        PathogenCodes.mycoplasma_ureaplasma,
        PathogenCodes.lactobacilli,
        PathogenCodes.anaerobic_bacteria,
        PathogenCodes.candida,
        PathogenCodes.hpv_screening,
        PathogenCodes.herpes_with_types
      ],
      [ScreeningType.Vms]: [
        PathogenCodes.lactobacilli,
        PathogenCodes.anaerobic_bacteria,
        PathogenCodes.candida,
        PathogenCodes.mycoplasma_ureaplasma,
        PathogenCodes.chlamydia,
        PathogenCodes.gonorrhoea,
        PathogenCodes.trichomoniasis,
        PathogenCodes.hpv_screening,
        PathogenCodes.herpes_with_types
      ]
    };

    pathogensData.sort((a, b) => screeningOrder[screeningType].indexOf(a.code) - screeningOrder[screeningType].indexOf(b.code));
  }

  if (country) {
    pathogensData = pathogensData.map((pathogen) => {
      if (pathogen.comingSoonCountryFilter && showInCountryCondition(pathogen.comingSoonCountryFilter, country)) {
        return { ...pathogen, comingSoon: true };
      }

      return { ...pathogen, comingSoon: pathogen.comingSoon || false };
    });
  }

  if (!codes) {
    return pathogensData;
  }

  return pathogensData.filter((pathogen) => codes.includes(pathogen.code));
}
